import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Anchor, LogOut, User as UserIcon, Star, MessageSquare } from "lucide-react";

const UNREAD_POLL_MS = 15000;

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return undefined; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/conversations/unread-count");
        if (!cancelled) setUnread(data.unread || 0);
      } catch (e) {
        // Non-critical: badge failures shouldn't spam console.
        // Log only if it's not a routine 401 (user got logged out).
        if (e?.response?.status && e.response.status !== 401) {
          console.warn("[Header] unread poll failed:", e.response.status);
        }
      }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, UNREAD_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-border/70 bg-background/80 backdrop-blur-xl"
    >
      <div className="container-editorial flex h-16 items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-2 font-display text-2xl tracking-tight"
        >
          <Anchor className="h-5 w-5 text-accent" strokeWidth={1.5} />
          <span>SlipShare</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <NavLink to="/search" data-testid="nav-search" className={({isActive}) => `link-underline ${isActive ? "text-primary font-medium" : "text-foreground/70"}`}>Browse Docks</NavLink>
          <NavLink to="/list" data-testid="nav-list" className={({isActive}) => `link-underline ${isActive ? "text-primary font-medium" : "text-foreground/70"}`}>List Your Dock</NavLink>
          {user && (
            <>
              <NavLink to="/messages" data-testid="nav-messages" className={({isActive}) => `link-underline inline-flex items-center gap-1.5 ${isActive ? "text-primary font-medium" : "text-foreground/70"}`}>
                Messages
                {unread > 0 && (
                  <span data-testid="header-unread-badge" className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-accent text-accent-foreground text-[10px] font-medium px-1">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </NavLink>
              <NavLink to="/dashboard" data-testid="nav-dashboard" className={({isActive}) => `link-underline ${isActive ? "text-primary font-medium" : "text-foreground/70"}`}>Dashboard</NavLink>
            </>
          )}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              {/* Mobile-only messages icon */}
              <button
                onClick={() => navigate("/messages")}
                data-testid="mobile-messages-btn"
                className="md:hidden relative p-2 rounded-full hover:bg-muted"
                aria-label="Messages"
              >
                <MessageSquare className="h-5 w-5" strokeWidth={1.5} />
                {unread > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-[16px] rounded-full bg-accent text-accent-foreground text-[9px] font-medium px-1 inline-flex items-center justify-center">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
              <span data-testid="user-name" className="hidden sm:flex items-center gap-2 text-sm text-foreground/70">
                <UserIcon className="h-4 w-4" strokeWidth={1.5}/>
                {user.name}
                {(user.qualified_referrals_count || 0) > 0 && (
                  <Star data-testid="referrer-star" className="h-3.5 w-3.5 text-accent fill-accent" strokeWidth={1.5} title="Verified referrer"/>
                )}
              </span>
              <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/"); }}>
                <LogOut className="h-4 w-4 mr-1" strokeWidth={1.5}/> Sign out
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="header-login-btn" variant="ghost" size="sm" onClick={() => navigate("/login")}>Log in</Button>
              <Button data-testid="header-register-btn" size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => navigate("/register")}>Sign up</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
